import {
  DatePicker,
  FormField,
  Input,
  NumberStepper,
  SegmentedControl,
  Select,
  Textarea,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { useColonies, useLocations } from '../../apiary/api';
import { ColonyPicker } from '../../apiary/components/ColonyPicker';
import { useCustomers } from '../../crm/api';
import { useSession } from '../../identity/session';
import { useAssignees, useSaveTask, type RecurrenceUnit, type Task } from '../api';
import styles from './tasks.module.css';

type ReminderChoice = 'none' | 'morning' | 'day_before' | 'custom' | 'at';
type RecurrenceChoice = 'none' | 'day' | 'week' | 'two_weeks' | 'month' | 'year' | 'custom';

/** Prefilled references when a task is created from a record (TSK-002). */
export interface TaskContext {
  colonyIds?: string[];
  locationId?: string;
  customerId?: string;
  saleId?: string;
  /** Names a sale reference, e.g. "Verkauf vom 20.10.2026 an Erika Muster". */
  label?: string;
}

export interface TaskFormDialogProps {
  open: boolean;
  onClose: () => void;
  task?: Task;
  context?: TaskContext;
  onSaved?: (task: Task) => void;
}

function reminderChoice(task?: Task): ReminderChoice {
  const r = task?.reminder;
  if (!r) return 'none';
  if (r.kind === 'at') return 'at';
  if (r.days === 0 && r.time?.startsWith('07:00')) return 'morning';
  if (r.days === 1 && r.time?.startsWith('18:00')) return 'day_before';
  return 'custom';
}

function recurrenceChoice(task?: Task): RecurrenceChoice {
  const r = task?.recurrence;
  if (!r) return 'none';
  if (r.every === 1) return r.unit;
  if (r.every === 2 && r.unit === 'week') return 'two_weeks';
  return 'custom';
}

/** Creates or edits a task with due date, reminder, assignee and references. */
export function TaskFormDialog({ open, onClose, task, context, onSaved }: TaskFormDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useSession();
  const save = useSaveTask();
  const assignees = useAssignees(open);
  const colonies = useColonies({}, open && can('colonies.read'));
  const locations = useLocations(false, open && can('locations.read'));
  const customers = useCustomers({}, open && can('customers.read'));
  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [dueOn, setDueOn] = useState(task?.due_on ?? '');
  const [dueTime, setDueTime] = useState(task?.due_time?.slice(0, 5) ?? '');
  const [reminder, setReminder] = useState<ReminderChoice>(reminderChoice(task));
  const [remindDays, setRemindDays] = useState<number | null>(task?.reminder?.days ?? 1);
  const [remindTime, setRemindTime] = useState(task?.reminder?.time?.slice(0, 5) ?? '07:00');
  const [remindAt, setRemindAt] = useState(task?.reminder?.at?.slice(0, 16) ?? '');
  const [assignee, setAssignee] = useState(task?.assignee?.id ?? '');
  const [priority, setPriority] = useState(task?.priority ?? 'normal');
  const [recurrence, setRecurrence] = useState<RecurrenceChoice>(recurrenceChoice(task));
  const [every, setEvery] = useState<number | null>(task?.recurrence?.every ?? 3);
  const [unit, setUnit] = useState<RecurrenceUnit>(task?.recurrence?.unit ?? 'day');
  const [colonyIds, setColonyIds] = useState<string[]>(
    task?.colonies.map((c) => c.id) ?? context?.colonyIds ?? [],
  );
  const [locationId, setLocationId] = useState(task?.location?.id ?? context?.locationId ?? '');
  const [customerId, setCustomerId] = useState(task?.customer?.id ?? context?.customerId ?? '');
  const saleId = task?.sale?.id ?? context?.saleId;
  const errors = formErrors(save.error, {
    title: ['task.title'],
    due: ['task.due_time', 'task.recurrence_without', 'task.reminder_without'],
    reminder: ['task.reminder'],
    recurrence: ['task.recurrence'],
    assignee: ['task.assignee'],
    refs: ['colony.', 'location.', 'customer.', 'sale.'],
  });
  const close = () => {
    save.reset();
    onClose();
  };

  const submit = () => {
    const reminderBody =
      reminder === 'none'
        ? null
        : reminder === 'at'
          ? { kind: 'at' as const, at: remindAt ? `${remindAt}:00` : null }
          : {
              kind: 'days_before' as const,
              days: reminder === 'morning' ? 0 : reminder === 'day_before' ? 1 : (remindDays ?? 0),
              time:
                reminder === 'morning'
                  ? '07:00:00'
                  : reminder === 'day_before'
                    ? '18:00:00'
                    : `${remindTime || '07:00'}:00`,
            };
    const recurrenceBody =
      recurrence === 'none'
        ? null
        : recurrence === 'custom'
          ? { unit, every: every ?? 1 }
          : recurrence === 'two_weeks'
            ? { unit: 'week' as const, every: 2 }
            : { unit: recurrence, every: 1 };
    save.mutate(
      {
        id: task?.id,
        version: task?.version,
        body: {
          title,
          description: description || null,
          due_on: dueOn || null,
          due_time: dueOn && dueTime ? `${dueTime}:00` : null,
          reminder: reminderBody,
          assignee_id: assignee || null,
          priority,
          recurrence: recurrenceBody,
          colony_ids: colonyIds,
          location_id: locationId || null,
          customer_id: customerId || null,
          sale_id: saleId ?? null,
        },
      },
      {
        onSuccess: (saved) => {
          toast.show(task ? t('tasks.form.saved') : t('tasks.form.created'));
          onSaved?.(saved);
          close();
        },
      },
    );
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      drawer
      title={task ? t('tasks.form.editTitle') : t('tasks.form.newTitle')}
      submitLabel={t('common.save')}
      onSubmit={submit}
      pending={save.isPending}
      error={errors.general}
    >
      <FormField label={t('tasks.form.title')} required error={errors.field.title}>
        {(p) => (
          <Input {...p} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        )}
      </FormField>
      <div className={styles.row}>
        <FormField label={t('tasks.form.dueOn')} error={errors.field.due}>
          {(p) => <DatePicker {...p} value={dueOn} onChange={setDueOn} />}
        </FormField>
        <FormField label={t('tasks.form.dueTime')}>
          {(p) => (
            <Input
              {...p}
              type="time"
              value={dueTime}
              disabled={!dueOn}
              onChange={(e) => setDueTime(e.target.value)}
            />
          )}
        </FormField>
      </div>
      <SegmentedControl
        label={t('tasks.form.priority')}
        value={priority}
        onChange={setPriority}
        options={[
          { value: 'normal', label: t('tasks.priority.normal') },
          { value: 'high', label: t('tasks.priority.high') },
        ]}
      />
      <FormField label={t('tasks.form.assignee')} error={errors.field.assignee}>
        {(p) => (
          <Select
            {...p}
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            options={[
              { value: '', label: t('tasks.form.nobody') },
              ...(assignees.data ?? []).map((a) => ({ value: a.id, label: a.name })),
            ]}
          />
        )}
      </FormField>
      <fieldset className={styles.section}>
        <legend>{t('tasks.form.reminder')}</legend>
        <FormField label={t('tasks.form.reminderWhen')} error={errors.field.reminder}>
          {(p) => (
            <Select
              {...p}
              value={reminder}
              onChange={(e) => setReminder(e.target.value as ReminderChoice)}
              options={(['none', 'morning', 'day_before', 'custom', 'at'] as ReminderChoice[]).map(
                (r) => ({
                  value: r,
                  label: t(`tasks.reminder.${r}`),
                  disabled: !dueOn && r !== 'none' && r !== 'at',
                }),
              )}
            />
          )}
        </FormField>
        {reminder === 'custom' && (
          <div className={styles.row}>
            <FormField label={t('tasks.form.daysBefore')}>
              {(p) => (
                <NumberStepper
                  {...p}
                  value={remindDays}
                  min={0}
                  max={60}
                  onChange={setRemindDays}
                  labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                />
              )}
            </FormField>
            <FormField label={t('tasks.form.at')}>
              {(p) => (
                <Input
                  {...p}
                  type="time"
                  value={remindTime}
                  onChange={(e) => setRemindTime(e.target.value)}
                />
              )}
            </FormField>
          </div>
        )}
        {reminder === 'at' && (
          <FormField label={t('tasks.form.remindAt')}>
            {(p) => (
              <Input
                {...p}
                type="datetime-local"
                value={remindAt}
                onChange={(e) => setRemindAt(e.target.value)}
              />
            )}
          </FormField>
        )}
      </fieldset>
      <fieldset className={styles.section}>
        <legend>{t('tasks.form.recurrence')}</legend>
        <FormField label={t('tasks.form.repeat')} error={errors.field.recurrence}>
          {(p) => (
            <Select
              {...p}
              value={recurrence}
              disabled={!dueOn}
              onChange={(e) => setRecurrence(e.target.value as RecurrenceChoice)}
              options={(
                [
                  'none',
                  'day',
                  'week',
                  'two_weeks',
                  'month',
                  'year',
                  'custom',
                ] as RecurrenceChoice[]
              ).map((r) => ({ value: r, label: t(`tasks.recurrence.${r}`) }))}
            />
          )}
        </FormField>
        {recurrence === 'custom' && (
          <div className={styles.row}>
            <FormField label={t('tasks.form.every')}>
              {(p) => (
                <NumberStepper
                  {...p}
                  value={every}
                  min={1}
                  max={365}
                  onChange={setEvery}
                  labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                />
              )}
            </FormField>
            <FormField label={t('tasks.form.unit')}>
              {(p) => (
                <Select
                  {...p}
                  value={unit}
                  onChange={(e) => setUnit(e.target.value as RecurrenceUnit)}
                  options={(['day', 'week', 'month', 'year'] as RecurrenceUnit[]).map((u) => ({
                    value: u,
                    label: t(`tasks.unit.${u}`),
                  }))}
                />
              )}
            </FormField>
          </div>
        )}
        {!dueOn && <p className={styles.hint}>{t('tasks.form.needsDate')}</p>}
      </fieldset>
      <fieldset className={styles.section} aria-invalid={errors.field.refs ? true : undefined}>
        <legend>{t('tasks.form.refs')}</legend>
        {can('locations.read') && (
          <FormField label={t('tasks.form.location')}>
            {(p) => (
              <Select
                {...p}
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                options={[
                  { value: '', label: t('tasks.form.none') },
                  ...(locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                ]}
              />
            )}
          </FormField>
        )}
        {can('colonies.read') && (colonies.data?.length ?? 0) > 0 && (
          <ColonyPicker
            colonies={colonies.data ?? []}
            selected={colonyIds}
            onChange={setColonyIds}
            locationId={locationId}
          />
        )}
        {can('customers.read') && (
          <FormField label={t('tasks.form.customer')}>
            {(p) => (
              <Select
                {...p}
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                options={[
                  { value: '', label: t('tasks.form.none') },
                  ...(customers.data ?? []).map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            )}
          </FormField>
        )}
        {saleId && <p className={styles.hint}>{task?.sale?.name ?? context?.label}</p>}
        {errors.field.refs && (
          <p className={styles.error} role="alert">
            {errors.field.refs}
          </p>
        )}
      </fieldset>
      <FormField label={t('tasks.form.description')}>
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </FormField>
    </FormDialog>
  );
}
